import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { gov } from "@/lib/governanceApi";
import { card, SectionTitle, Act, Field, errMsg } from "./ui";

const List = ({ title, items, testid, render }) => (
  <div className={`${card} p-4`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">{title}</span>
      <span className="font-display text-2xl font-extrabold text-[#14304F]" data-testid={`${testid}-count`}>{items.length}</span>
    </div>
    <ul className="text-sm space-y-1 max-h-40 overflow-auto">
      {items.length === 0 && <li className="text-zinc-400">None</li>}
      {items.map((x, i) => <li key={i} className="border-l-2 border-zinc-200 pl-2">{render ? render(x) : x}</li>)}
    </ul>
  </div>
);

const Provider = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["gov-provider"], queryFn: gov.provider });
  const [form, setForm] = useState(null);
  const f = form || data || {};
  const save = async () => {
    try { await gov.saveProvider(f); toast.success("Provider details saved"); setForm(null); qc.invalidateQueries({ queryKey: ["gov-provider"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`${card} p-4`} data-testid="provider-profile">
      <SectionTitle sub="Used in every document. Leave [TO BE CONFIRMED] until verified. Never invent these details.">Provider identity</SectionTitle>
      <div className="grid md:grid-cols-3 gap-3">
        {Object.keys(data || {}).map((k) => (
          <Field key={k} name={k} value={f[k]} testid={`provider-${k}`} onChange={(n, v) => setForm({ ...f, [n]: v })} />
        ))}
      </div>
      <div className="mt-3"><Act onClick={save} data-testid="provider-save">Save provider details</Act></div>
    </div>
  );
};

export default function Overview() {
  const { data: o, isLoading } = useQuery({ queryKey: ["gov-overview"], queryFn: gov.overview });
  if (isLoading || !o) return <div className="font-mono text-sm text-zinc-400">Loading…</div>;
  const chain = o.audit_chain;
  return (
    <div className="space-y-5" data-testid="gov-overview">
      <div className={`${card} p-4 flex items-center gap-3 ${chain.intact ? "" : "border-red-600"}`} data-testid="audit-chain-status">
        {chain.intact ? <ShieldCheck className="text-emerald-600" /> : <ShieldAlert className="text-red-600" />}
        <div className="text-sm"><strong>Audit trail {chain.intact ? "intact" : `BROKEN at #${chain.broken_at_seq}`}</strong> — {chain.entries} chained entries verified.</div>
      </div>
      <p className="text-xs text-zinc-500 max-w-3xl" data-testid="overview-methodology">{o.methodology}</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <List title="Documents not yet approved" items={o.documents_not_approved} testid="ov-docs-unapproved" />
        <List title="Awaiting review" items={o.documents_awaiting_review} testid="ov-docs-review" />
        <List title="Reviews due (30 days)" items={o.documents_review_due} testid="ov-docs-due" />
        <List title="Organisations missing agreements" items={o.organisations_missing_agreements} testid="ov-orgs-missing" render={(x) => `${x.team}: ${x.missing.join(", ").toUpperCase()}`} />
        <List title="DPIA screenings outstanding" items={o.dpia_outstanding} testid="ov-dpia" />
        <List title={`Open incidents (${o.open_high_incidents} high/critical)`} items={o.open_incidents} testid="ov-incidents" render={(x) => `[${x.severity}] ${x.title}`} />
      </div>
      <div className={`${card} p-4`} data-testid="ov-controls">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-3">Evidence register — requirements by status</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(o.controls_by_status).map(([k, v]) => (
            <div key={k} className="border border-zinc-200 px-3 py-2 flex justify-between text-sm"><span>{k}</span><strong>{v}</strong></div>
          ))}
        </div>
      </div>
      <List title="Evidence gaps" items={o.evidence_gaps} testid="ov-gaps" />
      <Provider />
    </div>
  );
}
